import { NOTE_FIELDS, conflictLabel, noteIsEmpty, updatedLabel } from "../../lib/assistNotes";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Textarea } from "../ui/textarea";
import { AlertTriangle, Check, Loader2 } from "lucide-react";

function SaveState({ state, onRetry }) {
  if (state === "saving") {
    return <span className="inline-flex items-center gap-1 text-slate-500"><Loader2 size={12} className="animate-spin" aria-hidden="true" />Enregistrement…</span>;
  }
  if (state === "saved") {
    return <span className="inline-flex items-center gap-1 text-slate-500"><Check size={12} aria-hidden="true" />Enregistré</span>;
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-red-700">
        Non enregistré
        <button type="button" onClick={onRetry} className="font-medium underline underline-offset-2 hover:text-red-900">Réessayer</button>
      </span>
    );
  }
  return null;
}

function Conflict({ conflict, onKeep, onTake, label }) {
  return (
    <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" role="alert" data-testid="assist-note-conflict">
      <p className="flex items-start gap-1.5 font-medium text-amber-900">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />{conflictLabel(conflict)}
      </p>
      <p className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded border border-amber-200 bg-white px-2 py-1.5 text-slate-800"
        aria-label={`Texte de ${conflict.updated_by?.name || "votre collègue"} pour ${label}`}>
        {conflict.value || <span className="text-slate-400">(champ vidé)</span>}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onTake} data-testid="assist-note-take">Reprendre son texte</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onKeep} data-testid="assist-note-keep">Garder mon texte</Button>
      </div>
    </div>
  );
}

// The fields of the shared note (hooks/useAssistNotes.js), the same wherever it is shown: column next to the screen, panel in
// full screen, phone sheet, report section after the session. Read-only for someone who may only read it.
export default function AssistNotesPanel({ notes, idPrefix = "assist-note" }) {
  if (notes.phase === "loading") {
    return <div className="space-y-3" data-testid="assist-notes-loading"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>;
  }
  if (notes.phase === "error") {
    return (
      <div className="space-y-2 text-sm text-slate-600" data-testid="assist-notes-error">
        <p>Impossible de charger les notes.</p>
        <Button size="sm" variant="outline" onClick={notes.reload}>Réessayer</Button>
      </div>
    );
  }
  if (!notes.canEdit) {
    if (noteIsEmpty(notes.note)) {
      return <p className="text-sm text-slate-500" data-testid="assist-notes-empty">Aucun compte rendu pour cette assistance.</p>;
    }
    return (
      <dl className="space-y-3" data-testid="assist-notes-readonly">
        {NOTE_FIELDS.map(({ key, label }) => {
          const field = notes.fields[key];
          if (!field.value.trim()) return null;
          return (
            <div key={key}>
              <dt className="text-xs font-medium text-slate-500">{label}</dt>
              <dd className="whitespace-pre-wrap break-words text-sm text-slate-900">{field.value}</dd>
              <dd className="text-2xs text-slate-400">{updatedLabel(field)}</dd>
            </div>
          );
        })}
      </dl>
    );
  }
  return (
    <div className="space-y-4" data-testid="assist-notes-form">
      {NOTE_FIELDS.map(({ key, label, max, rows, placeholder }) => {
        const field = notes.fields[key];
        const id = `${idPrefix}-${key}`;
        const nearLimit = field.value.length > max * 0.8;
        return (
          <div key={key} className="space-y-1.5">
            <label htmlFor={id} className="text-sm font-medium text-slate-800">{label}</label>
            <Textarea id={id} rows={rows} maxLength={max} placeholder={placeholder} value={field.value}
              onChange={(event) => notes.update(key, event.target.value)} onBlur={() => notes.flush(key)}
              className="resize-y bg-white" data-testid={`assist-note-${key}`} />
            <div className="flex min-h-[16px] flex-wrap items-center justify-between gap-x-3 text-2xs">
              <span className="text-slate-400" data-testid={`assist-note-${key}-updated`}>{updatedLabel(field)}</span>
              <span className="flex items-center gap-3">
                {nearLimit && <span className="text-slate-500 tabular-nums">{field.value.length} / {max}</span>}
                <span data-testid={`assist-note-${key}-state`}><SaveState state={notes.status[key]} onRetry={() => notes.retry(key)} /></span>
              </span>
            </div>
            {field.conflict && (
              <Conflict conflict={field.conflict} label={label} onKeep={() => notes.keepMine(key)} onTake={() => notes.takeTheirs(key)} />
            )}
          </div>
        );
      })}
    </div>
  );
}
